import React, { useEffect, useRef, useState } from 'react';
import { TextField } from '@fluentui/react/lib/TextField';
import { CommandBar, Checkbox, DatePicker, DefaultButton, Dialog, DialogFooter, DialogType, Dropdown, FontWeights, IBasePickerSuggestionsProps, ICommandBarItemProps, IDropdownOption, IStackStyles, ITag, Label, MessageBar, MessageBarType, PrimaryButton, Stack, TagPicker } from '@fluentui/react';
import { CoherencePanelSize } from '@coherence-design-system/utilities';
import { CoherenceNotificationType, CoherencePanel } from '@coherence-design-system/controls';
import { SearchableDropdown } from '../CustomSearchDropdown/CustomSearchDropdown';
import { getUserFilterPreference, saveUserFilterPreference, deleteUserFilterPreference, listUserFilterPresets, isEmpty, onFormatDate } from '../../utils/OPPApiHelper';
import dayjs from 'dayjs';
import ToastMessage from '../ToastMessage/ToastMessage';
import { useToast } from '../../hooks/useToast';
import { getCurrentUserName } from "../../utils/CurrentUser";
import { persistGridSortPreference, resolveGridSortPreference } from "./gridSortPreferences";
import ActiveFilterChips, { ActiveFilterChip } from "../ServerSideGridControls/ActiveFilterChips";

interface ActiveFilterColumn {
  fieldName: string;
  label: string;
  type: string;
}

interface prop {
  filterColumns: any;
  sortColumns: any;
  data: any;
  onApplyFilter: any
  onClearFilter: any;
  onSorted: any;
  additionalControls?:any;
  onClickAdditionalControls?:any;
  defaultFilterSessionKey:any;
  enableSavedFilters?: boolean;
  onClearSorting?: () => void;
  sortPreferenceKey?: string;
  hasActiveSorting?: boolean;
  keepCommandsVisible?: boolean;
}

const MAX_SAVED_FILTER_NAME_LENGTH = 100;

interface SavedFilterPreset {
  name: string;
  filterKey: string;
  values: Record<string, any>;
}

const hasAnyFilterValue = (values: Record<string, any> | undefined): boolean =>
  Object.values(values ?? {}).some((value) => (Array.isArray(value) ? value.length > 0 : value !== '' && value !== null && value !== undefined));

const CustomGridControls = (props: prop) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [filteredValues, setFilteredValues] = useState<any>();
  const [appliedFilterValues, setAppliedFilterValues] = useState<any>();
  const { showToast, toastTitle, toastMessage, toastType, showToastMessage } = useToast();       
  const [serverPrefsLoaded, setServerPrefsLoaded] = useState(false);
  const [savedPresets, setSavedPresets] = useState<SavedFilterPreset[]>([]);
  const [activePresetName, setActivePresetName] = useState<string | null>(null);
  const [defaultPresetName, setDefaultPresetName] = useState<string | null>(null);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [saveNameInput, setSaveNameInput] = useState('');
  const [saveSetAsDefault, setSaveSetAsDefault] = useState(true);
  const [saveNameError, setSaveNameError] = useState<string | null>(null);
  const [isSavingPreset, setIsSavingPreset] = useState(false);
  const savePresetInFlightRef = useRef(false);
  const [presetToDelete, setPresetToDelete] = useState<string | null>(null);
  const [isDeletingPreset, setIsDeletingPreset] = useState(false);
  const deletePresetInFlightRef = useRef(false);
  const lastPresetToDeleteRef = useRef('');
  if (presetToDelete) lastPresetToDeleteRef.current = presetToDelete;
  const savedFiltersLoadedRef = useRef(false);

  function parseDateValue(val: any): Date | null {
    if (!val) return null;
    if (val instanceof Date) return val;
    // If it's a string, try to parse
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }

  // Load saved filter preferences from server on mount
  useEffect(() => {
    if (!isEmpty(props.defaultFilterSessionKey)) {
      getUserFilterPreference(props.defaultFilterSessionKey).then((savedFilters: any) => {
        if (!isEmpty(savedFilters)) {
          setFilteredValues(savedFilters);
          setAppliedFilterValues(savedFilters);
        }
        setServerPrefsLoaded(true);
      });
    } else {
      setServerPrefsLoaded(true);
    }
  }, []);

  useEffect(() => { 
    const { data, filterColumns } = props; 
    if(data && data.length > 0 && serverPrefsLoaded){
       if(isEmpty(filteredValues) || Object.keys(filteredValues).length == 0){ 
        const byDefaultFilter = filterColumns.filter((x:any) => x.Default != null);
        if(byDefaultFilter.length > 0){   
          let newFilterValues = { ...filteredValues};
          byDefaultFilter.forEach((element:any) => {
            newFilterValues = { ...newFilterValues, [element.fieldName]: element.Default };
          });  
          setFilteredValues(newFilterValues);
          setAppliedFilterValues(newFilterValues);
          onClickApplyFilter(newFilterValues);    
        }  
      }
      else{
        onClickApplyFilter(filteredValues);
      }    
    } 
  }, [props.data, serverPrefsLoaded]);

  const onClickApplyFilter = (inputFilteredValues:any) => {

    const filteredValuesObj = inputFilteredValues || { ...filteredValues }; 
    const hasFilterInputValue = Object.values(filteredValuesObj).some((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '' && value !== null;
    });
    if (!hasFilterInputValue) {
      if (inputFilteredValues != null) {
        props.onClearFilter();
        setIsFilterApplied(false);
      }
      return;
    }

    setIsFilterApplied(true);
    setIsPanelOpen(false);

    let filteredData: any = [];
    let filteredDataAssigned: boolean=false; 
    props.filterColumns.forEach((item: any) => {
      const fieldValue = filteredValuesObj[item.fieldName];
      switch (item.type) {
        case 'text':
          case 'dropdown':
          case 'searchDropdown':
            case 'searchTextBox':
            if (!isEmpty(fieldValue)) {
              //if filter is applied on login user as assigned to then filter the groups which the user is  memeber of.  
              if(item.fieldName== "AssignedToUserName" && fieldValue==getCurrentUserName()){
                filteredData = (filteredDataAssigned ? filteredData : props.data).filter((data:any) => data[item.fieldName]?.toString() === fieldValue || data.IsMemberOfGroup==true);
              }else{
                filteredData = (filteredDataAssigned ? filteredData : props.data).filter((data:any) => data[item.fieldName]?.toString() === fieldValue);
              }
              filteredDataAssigned = true;
            }
            break;
        case "multiselectDropdown":
          if (!isEmpty(fieldValue)) {
            var filteredDropdownData: any = [];
            fieldValue.forEach((value: any) => {
              filteredDropdownData = filteredDropdownData.concat((filteredDataAssigned ? filteredData : props.data).filter((data: any) => data[item.fieldName] === value));
            });
            filteredData = filteredDropdownData;
            filteredDataAssigned=true;
          }
          break;
        case "date":
          if (!isEmpty(filteredValuesObj["from" + item.fieldName])) {
            filteredData = (filteredDataAssigned ? filteredData : props.data).filter((data: any) => {
              const fromDate = parseDateValue(filteredValuesObj["from" + item.fieldName]);
              const dateValue = parseDateValue(data[item.fieldName]);
              if (!fromDate || !dateValue) return false;
              fromDate.setHours(0, 0, 0, 0);
              dateValue.setHours(0, 0, 0, 0);
              return dateValue >= fromDate;
            });
            filteredDataAssigned = true;
          }
          if (!isEmpty(filteredValuesObj["to" + item.fieldName])) {
            filteredData = (filteredDataAssigned ? filteredData : props.data).filter((data: any) => {
              const toDate = parseDateValue(filteredValuesObj["to" + item.fieldName]);
              const dateValue = parseDateValue(data[item.fieldName]);
              if (!toDate || !dateValue) return false;
              toDate.setHours(23, 59, 59, 999);
              dateValue.setHours(23, 59, 59, 999);
              return dateValue <= toDate;
            });
            filteredDataAssigned = true;
          }
          break;
          case 'contains':
            if (!isEmpty(fieldValue)) {
              var filteredDropdownData: any = [];
              fieldValue.forEach((value: any) => {
                filteredDropdownData = filteredDropdownData.concat((filteredDataAssigned ? filteredData : props.data).filter((data: any) => data[item.fieldName].includes(value)));
              });
              filteredData = filteredDropdownData;
              filteredDataAssigned=true;
            } 
          break;
      }
    });
    const uniqueArray = Array.from(new Set(filteredData.map((item: any) => item.Id))).map((Id: any) => {
      return filteredData.find((item: any) => item.Id === Id);
    });
    setAppliedFilterValues(filteredValuesObj);
    props.onApplyFilter(uniqueArray);
  }
  const onClickSaveUserPref = async () => { 
    const filteredValuesObj = { ...filteredValues };    
    const success = await saveUserFilterPreference(props.defaultFilterSessionKey, isEmpty(filteredValuesObj)?[]:filteredValuesObj);
    if (success) {
      showToastMessage("Filter Panel","Default filter saved successfully.",CoherenceNotificationType.SUCCESS);
    } else {
      showToastMessage("Filter Panel","Failed to save default filter.",CoherenceNotificationType.ERROR);
    }
  }

  const savedFiltersEnabled = !!props.enableSavedFilters && !isEmpty(props.defaultFilterSessionKey);
  // Presets are stored as "<defaultKey>|Preset|<name>"; the default pointer is a separate row.
  const presetKeyPrefix = `${props.defaultFilterSessionKey}|Preset|`;
  const presetDefaultPointerKey = `${props.defaultFilterSessionKey}|PresetDefault`;

  const refreshSavedPresets = async () => {
    const [rows, pointer] = await Promise.all([
      listUserFilterPresets(presetKeyPrefix),
      getUserFilterPreference(presetDefaultPointerKey)
    ]);
    setSavedPresets(rows.map((row) => ({
      name: row.filterKey.slice(presetKeyPrefix.length),
      filterKey: row.filterKey,
      values: row.values
    })));
    const defaultName = typeof pointer?.name === 'string' ? pointer.name : null;
    setDefaultPresetName(defaultName);
    // The default filter is applied on load, so treat it as the active saved filter until another is chosen.
    setActivePresetName((current) => current ?? defaultName);
  };

  useEffect(() => {
    if (!savedFiltersEnabled || !serverPrefsLoaded || savedFiltersLoadedRef.current) {
      return;
    }
    savedFiltersLoadedRef.current = true;
    refreshSavedPresets().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFiltersEnabled, serverPrefsLoaded]);

  // Writes the default values (read on mount) and the pointer that marks which preset is default.
  const writeDefaultPreset = async (name: string, values: unknown): Promise<boolean> => {
    const valuesSaved = await saveUserFilterPreference(props.defaultFilterSessionKey, isEmpty(values) ? [] : values);
    const pointerSaved = await saveUserFilterPreference(presetDefaultPointerKey, { name });
    return valuesSaved && pointerSaved;
  };

  const getFilterSnapshot = (): Record<string, any> =>
    hasAnyFilterValue(filteredValues) ? { ...filteredValues } : { ...appliedFilterValues };

  const openSaveFilterDialog = (): void => {
    if (!hasAnyFilterValue(getFilterSnapshot())) {
      showToastMessage('Select a filter', 'Select at least one filter before saving it.', CoherenceNotificationType.WARNING);
      return;
    }
    setSaveNameInput('');
    setSaveSetAsDefault(true);
    setSaveNameError(null);
    setIsSaveDialogOpen(true);
  };

  const validateSavedFilterName = (raw: string): string | null => {
    const name = raw.trim();
    if (!name) return 'Enter a name for this saved filter.';
    if (name.length > MAX_SAVED_FILTER_NAME_LENGTH) {
      return `Name must be ${MAX_SAVED_FILTER_NAME_LENGTH} characters or fewer.`;
    }
    if (savedPresets.some((preset) => preset.name.toLowerCase() === name.toLowerCase())) {
      return `A saved filter named "${name}" already exists.`;
    }
    return null;
  };

  const saveFilterAsInternal = async (): Promise<void> => {
    const nameError = validateSavedFilterName(saveNameInput);
    if (nameError) {
      setSaveNameError(nameError);
      return;
    }
    const name = saveNameInput.trim();
    const values = getFilterSnapshot();
    if (!hasAnyFilterValue(values)) {
      setSaveNameError('Apply a filter first, then save it.');
      return;
    }
    showToastMessage('Saved Filters', 'Filter is being saved.', CoherenceNotificationType.PENDING);
    const saved = await saveUserFilterPreference(presetKeyPrefix + name, values);
    if (!saved) {
      setSaveNameError('Failed to save the filter. Please try again.');
      showToastMessage('Saved Filters', 'Failed to save the filter. Please try again.', CoherenceNotificationType.ERROR);
      return;
    }
    if (saveSetAsDefault && !(await writeDefaultPreset(name, values))) {
      showToastMessage('Saved Filters', 'Saved, but failed to set it as the default.', CoherenceNotificationType.ERROR);
      return;
    }
    showToastMessage('Saved Filters', `Filter "${name}" was saved.`, CoherenceNotificationType.SUCCESS);
    setIsSaveDialogOpen(false);
    setActivePresetName(name);
    await refreshSavedPresets();
    setFilteredValues(values);
    onClickApplyFilter(values);
  };

  const onClickSaveFilterAs = async (): Promise<void> => {
    if (savePresetInFlightRef.current) return;
    savePresetInFlightRef.current = true;
    setIsSavingPreset(true);
    try {
      await saveFilterAsInternal();
    } catch {
      setSaveNameError('Failed to save the filter. Please try again.');
      showToastMessage('Saved Filters', 'Failed to save the filter. Please try again.', CoherenceNotificationType.ERROR);
    } finally {
      savePresetInFlightRef.current = false;
      setIsSavingPreset(false);
    }
  };

  const activePreset = savedFiltersEnabled
    ? savedPresets.find((preset) => preset.name === activePresetName) ?? null
    : null;

  // Overwrites the active saved filter with the current panel selections; "Save Filter As" still creates a new one.
  const onClickUpdateActiveFilter = async (): Promise<void> => {
    if (!activePreset || savePresetInFlightRef.current) return;
    savePresetInFlightRef.current = true;
    setIsSavingPreset(true);
    try {
      showToastMessage('Saved Filters', 'Filter is being updated.', CoherenceNotificationType.PENDING);
      const values = getFilterSnapshot();
      if (!hasAnyFilterValue(values)) {
        showToastMessage('Select a filter', 'Select at least one filter before saving it.', CoherenceNotificationType.WARNING);
        return;
      }
      if (!(await saveUserFilterPreference(activePreset.filterKey, values))) {
        showToastMessage('Saved Filters', `Failed to update "${activePreset.name}".`, CoherenceNotificationType.ERROR);
        return;
      }
      if (activePreset.name === defaultPresetName && !(await writeDefaultPreset(activePreset.name, values))) {
        showToastMessage('Saved Filters', 'Updated, but failed to refresh the default filter.', CoherenceNotificationType.ERROR);
      } else {
        showToastMessage('Saved Filters', `Filter "${activePreset.name}" was updated.`, CoherenceNotificationType.SUCCESS);
      }
      await refreshSavedPresets();
      setFilteredValues(values);
      onClickApplyFilter(values);
    } catch {
      showToastMessage('Saved Filters', `Failed to update "${activePreset.name}".`, CoherenceNotificationType.ERROR);
    } finally {
      savePresetInFlightRef.current = false;
      setIsSavingPreset(false);
    }
  };

  const onClickApplyPreset = (preset: SavedFilterPreset): void => {
    setActivePresetName(preset.name);
    setFilteredValues(preset.values);
    onClickApplyFilter(preset.values);
  };

  const onClickSetPresetAsDefault = async (preset: SavedFilterPreset): Promise<void> => {
    let success = false;
    try {
      success = await writeDefaultPreset(preset.name, preset.values);
      await refreshSavedPresets();
    } catch {
      success = false;
    }
    if (success) {
      showToastMessage('Saved Filters', `"${preset.name}" is now your default filter.`, CoherenceNotificationType.SUCCESS);
    } else {
      showToastMessage('Saved Filters', 'Failed to set the default filter.', CoherenceNotificationType.ERROR);
    }
  };

  const onConfirmDeletePreset = async (): Promise<void> => {
    if (deletePresetInFlightRef.current) return;
    deletePresetInFlightRef.current = true;
    setIsDeletingPreset(true);
    try {
      const preset = savedPresets.find((item) => item.name === presetToDelete);
      if (!preset) return;
      const deleted = await deleteUserFilterPreference(preset.filterKey);
      if (!deleted) {
        showToastMessage('Delete Filter', 'Failed to delete the saved filter.', CoherenceNotificationType.ERROR);
        return;
      }
      if (preset.name === defaultPresetName) {
        await deleteUserFilterPreference(props.defaultFilterSessionKey);
        await deleteUserFilterPreference(presetDefaultPointerKey);
      }
      if (preset.name === activePresetName) {
        setActivePresetName(null);
      }
      try {
        await refreshSavedPresets();
      } catch {
        setSavedPresets((current) => current.filter((item) => item.filterKey !== preset.filterKey));
      }
      showToastMessage('Delete Filter', `Filter "${preset.name}" was deleted.`, CoherenceNotificationType.SUCCESS);
    } catch {
      showToastMessage('Delete Filter', 'Failed to delete the saved filter.', CoherenceNotificationType.ERROR);
    } finally {
      deletePresetInFlightRef.current = false;
      setIsDeletingPreset(false);
      setPresetToDelete(null);
    }
  };
  const onClickClearFilter = () => {
    const newFilterValues = { ...filteredValues }; // create a copy of the filter values object
    props.filterColumns.forEach((element: any) => {
      if (element.type === 'dropdown' || element.type === 'searchDropdown' || element.type === 'searchTextBox') {
        newFilterValues[element.fieldName] = null; // set the value to empty array
      }
      if (element.type === 'multiselectDropdown' || element.type === 'contains') {
        newFilterValues[element.fieldName] = []; // set the value to empty array
      }
      else if (element.type === 'date') {
        newFilterValues["from" + element.fieldName] = null; // set the from date value to empty 
        newFilterValues["to" + element.fieldName] = null; // set the to date value to empty 
      }
      else {
        newFilterValues[element.fieldName] = ""; // set the value of each filter field to an empty string
      }
    });
    setFilteredValues(newFilterValues); // update the state of the filter values object
    setAppliedFilterValues(newFilterValues);
    setIsFilterApplied(false);
    // A cleared panel no longer corresponds to any saved filter, so hide "Update Filter".
    setActivePresetName(null);
    props.onClearFilter();
  }

  function handleFromDateChangeFactory(object: any) {
    return function handleDropdownChange(selectedDate: any) {
      const newFilterValues = { ...filteredValues, ["from" + object.fieldName]: selectedDate };
      setFilteredValues(newFilterValues);
     
    }
  }
function handleToDateChangeFactory(object: any) {
  return function handleDropdownChange(selectedDate: any) {
    const newFilterValues = { ...filteredValues, ["to" + object.fieldName]: selectedDate };
      setFilteredValues(newFilterValues);

  }
}

  function handleDropdownChangeFactory(object: any) {
    return function handleDropdownChange(event: React.FormEvent<HTMLDivElement>, option: IDropdownOption | undefined) {
      if (!option) return;

      const newFilterValues = { ...filteredValues, [object.fieldName]: option.key };
      setFilteredValues(newFilterValues);
    }
  }

  function handleMultiSelectDropdownChangeFactory(object: any) {
    return function handleDropdownChange(event: React.FormEvent<HTMLDivElement>, option: IDropdownOption | undefined) {
      if (!option) return;
      let selectedValues:any =[];
       
      if(!isEmpty(filteredValues[object.fieldName])){
        selectedValues= [...filteredValues[object.fieldName]];
      }
      
      if (option.selected) {
        selectedValues.push(option.key);
      }
      else {
        selectedValues = selectedValues.filter((item: any) => item !== option.key);
      }

      const newFilterValues = { ...filteredValues, [object.fieldName]: selectedValues };
      setFilteredValues(newFilterValues);
    }
  }

  const generateControls = (): any => {
    let tmpRenderObj: any[] = [];
    if (props.filterColumns === undefined)
      return;
    props.filterColumns.forEach((object: any) => {
      if ( object.type === "contains" || object.type === "multiselectDropdown" || object.type === "dropdown" || object.type === "searchDropdown" || object.type === "searchTextBox") {
        let options: any[] = [];
        if (object.type === "contains") {
          const optionSet = new Set();
          props.data.forEach((item: any) => {
            const fieldValues = item[object.fieldName]?.toString();
            if (fieldValues?.includes(',')) {
              fieldValues.split(',').forEach((value: string) => optionSet.add(value.trim()));
            } else {
              optionSet.add(fieldValues);
            }
          });
          options = Array.from(optionSet).map(value => ({ key: value, text: value }));
        } else {
          options = props.data.map((item: any) => ({
            key: item[object.fieldName]?.toString(),
            text: item[object.fieldName]?.toString()
          }));
        }
        options = options.filter((x: any) => x.key !== undefined && x.key !== null && x.key !== "");
        options = [...new Map(options.map((item: any) => [item["key"], item])).values()];
        object.options = [...options].sort((a, b) => {
          return a.key.localeCompare(b.key);
        }); 
      }
    });
    
    //search Textbox code start
const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
  suggestionsHeaderText: 'Suggested Result(s)', 
  noResultsFoundText:'No results found'
};

const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
  if (!tagList || !tagList.length || tagList.length === 0) {
    return false;
  }
  return tagList.some(compareTag => compareTag.key === tag.key);
};
 
const filterSuggestedTags = async (filterText: string, tagList?: ITag[] | undefined, object?: any): Promise<ITag[]> => {
  const suggestionsHeaderText = 'Suggested Result(s)';
  let minChar=0;
  let noResultsFoundText='No results found'; 
  if(!isEmpty(filterText) && !isEmpty(object.additionalData)){
    minChar=object.additionalData.minChar;
    noResultsFoundText = filterText.length >= minChar ? 'No results found' : 'Enter a minimum of '+minChar+' characters for result(s)';
  } 
  // Update pickerSuggestionsProps dynamically
  pickerSuggestionsProps.suggestionsHeaderText = suggestionsHeaderText;
  pickerSuggestionsProps.noResultsFoundText = noResultsFoundText;
  // Access 'object' and use it as needed 
  let result=[]; 
  if(!isEmpty(filterText)&& filterText.length >= minChar){
  result=  object.options.filter(
      (tag:any) => tag.text.toLowerCase().indexOf(filterText.toLowerCase()) >= 0 && !listContainsTagList(tag, tagList),
    ).map((item:any) => ({ name: item.text })) 
  }
  return result;
}
//search Textbox code end

    const getTextFromItem = (item: ITag) => item.name; 
    props.filterColumns.forEach((object: any) => {
      switch (object.type) {
        case 'text':
          {
            tmpRenderObj.push(
              <TextField
                label={object.label}
                value={filteredValues[object.fieldName]}
                onChange={(event, newValue) => {
                  const newFilterValues = { ...filteredValues };
                  newFilterValues[object.fieldName] = newValue || "";
                  setFilteredValues(newFilterValues);
                }}
                />

            );
            break
          }
        case 'dropdown':
          {
            tmpRenderObj.push(
              <Dropdown
                label={object.label}
                options={object.options}
                placeholder={"Select " + object.label}
                required={false}
                selectedKey={filteredValues[object.fieldName]}
                onChange={handleDropdownChangeFactory(object)}
              />
            );
            break
          }
        case 'multiselectDropdown':
        case 'contains':
          {
            tmpRenderObj.push(
              <Dropdown
                label={object.label}
                options={object.options}
                placeholder={"Select " + object.label}
                required={false}
                multiSelect
                selectedKeys={filteredValues[object.fieldName]}
                onChange={handleMultiSelectDropdownChangeFactory(object)}
              />
            );
            break
          }
        case 'searchDropdown':
          {
            tmpRenderObj.push(
              <SearchableDropdown
                placeholder={"Select " + object.label}
                label={object.label}
                options={object.options}
                // styles={dropdownStyles}
                multiSelect={false}
                selectedKey={filteredValues[object.fieldName]}
                onChange={handleDropdownChangeFactory(object)}
              />
            );
            break
          }
        case 'date':
          {
            tmpRenderObj.push(
                <DatePicker
                allowTextInput={false}
                label={"From " + object.label}
                value={parseDateValue(filteredValues["from" + object.fieldName]) || undefined}
                placeholder={"Select From " + object.label}
                onSelectDate={handleFromDateChangeFactory(object)}
                formatDate={onFormatDate}
                ></DatePicker>
            );
            tmpRenderObj.push(
              <DatePicker
              allowTextInput={false}
              value={parseDateValue(filteredValues["to" + object.fieldName]) || undefined}
              label={"To " + object.label}
              placeholder={"Select To " + object.label}
              minDate={parseDateValue(filteredValues["from" + object.fieldName]) || undefined}
              onSelectDate={handleToDateChangeFactory(object)}
              formatDate={onFormatDate} 
              ></DatePicker>
            );
            break
          }
          case 'searchTextBox':
            {
              tmpRenderObj.push(
              <div> 
              <Label>{object.label}</Label>
                     <TagPicker  
                    selectedItems={!isEmpty(filteredValues[object.fieldName]) ? [{ name: filteredValues[object.fieldName], key: filteredValues[object.fieldName] }]:[]}
                     removeButtonAriaLabel="Remove"
                     selectionAriaLabel={"Selected "+ (filteredValues!=undefined && filteredValues[object.fieldName])? filteredValues[object.fieldName]:''}
                     inputProps={{ 
                      'aria-label': 'Search by '+object.label,
                       placeholder:"Search by "+object.label
                   }}
                     onResolveSuggestions={(filterText, tagList) => filterSuggestedTags(filterText, tagList, object)}  
                     onChange={(items:any) => {
                        if(items.length>0){  
                            const newFilterValues = { ...filteredValues, [object.fieldName]: items[0].name };
                              setFilteredValues(newFilterValues);
                            }                
                           else{
                            const newFilterValues = { ...filteredValues, [object.fieldName]: "" };
                            setFilteredValues(newFilterValues);
                           }
                        }  
                     }
                  //   getTextFromItem={getTextFromItem}
                     pickerSuggestionsProps={pickerSuggestionsProps}
                     itemLimit={1}
                     // this option tells the picker's callout to render inline instead of in a new layer
                     pickerCalloutProps={{ doNotLayer: true,calloutMaxWidth: 350 }} 
                   /> 
                     </div>
              );
              break
            }
      }
    });


    return (
      <div style={{ display: "grid", gridTemplateColumns: "50% 50%", gridGap: "1rem" }}>
        {tmpRenderObj}
      </div>
    );
  }
  const handleOnPanelClose = (): void => {
    if (isSaveDialogOpen || presetToDelete !== null) return;
    setIsPanelOpen(false);
  };
  const onFilterPanelOpen = (): void => {
    if (filteredValues === undefined) {   
      const defaultValues: { [key: string]: any } = {};
      props.filterColumns.forEach((object: any) => {
        if (object.type === "contains" || object.type === "multiselectDropdown" || object.type === "dropdown" || object.type === "searchDropdown" || object.type === "searchTextBox") {
          defaultValues[object.fieldName] = [];
        }
        else {
          defaultValues[object.fieldName] = "";
        }
      });
      setFilteredValues(defaultValues);
      }

      setIsPanelOpen(true);

    };

  //Sort code start
  const onSorted = (columName: string, sortyType: string): void => {
    sortRestoredRef.current = true;
    props.onSorted(columName, sortyType);
    if (props.sortPreferenceKey) {
      setHasSavedSort(true);
      const sortType = sortyType === "desc" ? "desc" : "asc";
      void persistGridSortPreference(props.sortPreferenceKey, [{ columnName: columName, sortType }]).then(result => {
        if (!result.succeeded) {
          showToastMessage("Grid sorting", "Your sorting was applied, but the preference could not be saved.", CoherenceNotificationType.ERROR);
        }
      });
    }
  }

  const [hasSavedSort, setHasSavedSort] = useState(false);

  const onClearSorting = (): void => {
    if (props.onClearSorting) {
      props.onClearSorting();
      return;
    }
    if (!props.sortPreferenceKey) return;
    sortRestoredRef.current = true;
    setHasSavedSort(false);
    void persistGridSortPreference(props.sortPreferenceKey, []).then(result => {
      if (result.succeeded) {
        showToastMessage("Grid sorting", "Saved sorting was cleared. The default order will apply the next time you open this screen.", CoherenceNotificationType.SUCCESS);
      } else {
        showToastMessage("Grid sorting", "Failed to clear the saved sorting.", CoherenceNotificationType.ERROR);
      }
    });
  };

  // Restores the user's saved sort once, as soon as the grid has data.
  const savedSortRef = useRef<{ columnName: string; sortType: "asc" | "desc" } | null>(null);
  const sortRestoredRef = useRef(false);
  const [savedSortLoaded, setSavedSortLoaded] = useState(false);
  useEffect(() => {
    if (!props.sortPreferenceKey) return;
    let isCurrent = true;
    void resolveGridSortPreference(
      props.sortPreferenceKey,
      (props.sortColumns ?? []).map((column: any) => column.fieldName),
      []
    ).then(preference => {
      if (!isCurrent) return;
      savedSortRef.current = preference.sorts[0] ?? null;
      setSavedSortLoaded(true);
    }).catch(() => {
      if (isCurrent) setSavedSortLoaded(true);
    });
    return () => { isCurrent = false; };
    // eslint-disable-next-line
  }, [props.sortPreferenceKey]);

  useEffect(() => {
    if (!savedSortLoaded || sortRestoredRef.current) return;
    const saved = savedSortRef.current;
    if (!saved) {
      sortRestoredRef.current = true;
      return;
    }
    if (!Array.isArray(props.data) || props.data.length === 0) return;
    sortRestoredRef.current = true;
    setHasSavedSort(true);
    props.onSorted(saved.columnName, saved.sortType);
    // eslint-disable-next-line
  }, [savedSortLoaded, props.data]);
  //Sort code end
  
  const removeFilterFromUI = (propertyInput: any, fieldName: any) => {
    const newFilterValues = { ...filteredValues };
    const column = props.filterColumns.find((element: any) => element.fieldName === fieldName); 
    if (column) {
      if ((column.type === 'dropdown' || column.type === 'searchDropdown' || column.type === 'searchTextBox') && newFilterValues[propertyInput]) {
        newFilterValues[column.fieldName] = null;
      } else if ((column.type === 'multiselectDropdown' ||  column.type === 'contains') && newFilterValues[propertyInput]) {
        newFilterValues[column.fieldName] = [];
      } else if (column.type === 'date') {
        if (newFilterValues['from' + fieldName]) {
          newFilterValues['from' + column.fieldName] = null;
        } else if (newFilterValues['to' + fieldName]) {
          newFilterValues['to' + column.fieldName] = null;
        }
      } else {
        newFilterValues[column.fieldName] = '';
      }
    } 
    setFilteredValues(newFilterValues);
    setAppliedFilterValues(newFilterValues);
    onClickApplyFilter(newFilterValues);
  };
 
  const getActiveFilterChips = (): ActiveFilterChip[] => {
    const chips: ActiveFilterChip[] = [];
    for (const property in appliedFilterValues) {
      const value: unknown = appliedFilterValues[property];
      if (value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0)) continue;
      const column: ActiveFilterColumn | undefined = props.filterColumns.find((candidate: ActiveFilterColumn) =>
        candidate.fieldName === property || "to" + candidate.fieldName === property || "from" + candidate.fieldName === property);
      if (!column) continue;
      const label = column.type === "date"
        ? (property.startsWith("to") ? "To " : "From ") + column.label : column.label;
      const values = column.type === "date" ? [dayjs(appliedFilterValues[property]).format("MM/DD/YYYY")]
        : Array.isArray(value) ? value.map(entry => String(entry)) : [String(value)];
      chips.push({ key: property, label, values, onRemove: () => removeFilterFromUI(property, column.fieldName) });
    }
    return chips;
  };

  
  // Additional commands dispatch by text; supplied item and submenu onClick handlers are ignored.
  const createbuttonsfromInputProps = (): ICommandBarItemProps[] => {
    let commandBarItems: ICommandBarItemProps[] = [];
    if(props.additionalControls!=undefined){
      props.additionalControls.forEach((element:any) => {
        var item = {
          key: element.key,
          text: element.text,
          ariaLabel: element.ariaLabel,
          iconProps: element.iconProps,
          onClick: () => {
            props.onClickAdditionalControls(element.text);
        },
           disabled:  element.disabled,
           subMenuProps: element.subMenuProps             
        }
        if(item.subMenuProps !== undefined) {
          item.subMenuProps.items.forEach((subElement:any) => {
            subElement.onClick = () => {props.onClickAdditionalControls(subElement.text);}
          });
        }
        commandBarItems.push(item); 
      });
    } 
    return commandBarItems;
    }

  const createSavedFiltersMenuItem = (): ICommandBarItemProps | null => {
    if (!savedFiltersEnabled) return null;
    return {
      key: 'savedFilters',
      text: 'Saved Filters',
      ariaLabel: 'Saved Filters',
      iconProps: { iconName: 'Bookmark' },
      subMenuProps: {
        items: savedPresets.length === 0
          ? [{ key: 'noSavedFilters', text: 'No saved filters yet', disabled: true }]
          : savedPresets.map((preset) => {
            const isDefault = preset.name === defaultPresetName;
            return {
              key: preset.filterKey,
              text: isDefault ? `${preset.name} (Default)` : preset.name,
              iconProps: { iconName: preset.name === activePresetName ? 'CheckMark' : 'Bookmark' },
              subMenuProps: {
                items: [
                  { key: 'applySavedFilter', text: 'Apply', iconProps: { iconName: 'Filter' }, onClick: () => onClickApplyPreset(preset) },
                  { key: 'setSavedFilterDefault', text: 'Set as default', disabled: isDefault, iconProps: { iconName: 'Pin' }, onClick: () => void onClickSetPresetAsDefault(preset) },
                  {
                    key: 'deleteSavedFilter',
                    text: 'Delete',
                    iconProps: { iconName: 'Delete' },
                    onClick: () => { window.setTimeout(() => setPresetToDelete(preset.name), 0); }
                  }
                ]
              }
            };
          })
      }
    };
  };
  const createCommandBarItemProps = (): ICommandBarItemProps[] => {
    let commandBarItems: ICommandBarItemProps[] = [];
    commandBarItems=createbuttonsfromInputProps();
    commandBarItems.push(
        {  
          key: 'columnSortBy', 
          text: 'Sort by', 
          ariaLabel: 'Sort by',
          disabled: false,
          cacheKey: 'myColumnSortByCacheKey', 
          iconProps: { iconName: 'Sort'}, 
          subMenuProps: {
            items: [
              ...props.sortColumns.map((item:any) => {
              return {
                key: item.fieldName,
                text: item.label, 
                subMenuProps: {
                  items: [
                    {
                      key: 'ascending',
                      text: 'Ascending', 
                      onClick: () => onSorted(item.fieldName,"asc"),
                      iconProps: { iconName: 'Ascending'}
                    },
                    {
                      key: 'descending',
                      text: 'Descending', 
                      onClick: () => onSorted(item.fieldName,"desc"),
                      iconProps: { iconName: 'Descending'}
                    }  
                  ]      
                }
              };
              }),
              ...((props.onClearSorting || props.sortPreferenceKey)
                ? [{
                    key: 'clearSorting',
                    text: 'Clear Sorting',
                    ariaLabel: 'Clear Sorting',
                    iconProps: { iconName: 'Clear' },
                    disabled: props.onClearSorting
                      ? props.hasActiveSorting === false
                      : !hasSavedSort,
                    onClick: () => onClearSorting()
                  }]
                : [])
            ], 
          } 
      }
    );
    const savedFiltersMenuItem = createSavedFiltersMenuItem();
    if (props.filterColumns && props.filterColumns.length > 0) {
      commandBarItems.push(
        { 
          key: 'columnFilters',
          text: 'Filter',
          ariaLabel: 'Filter',
          disabled: false,
          cacheKey: 'myColumnFilterCacheKey',
          iconProps: { iconName: isFilterApplied?"FilterSolid":'Filter' },
          subMenuProps: {
            items: [
              {
                key: 'Filter',
                text: 'Filter',
                disabled: props.data === undefined || props.data.length === 0,
                iconProps: { iconName: 'Filter' },
                onClick: () => onFilterPanelOpen()
              },
              {
                key: 'clearFilters',
                text: 'Clear Filters',
                disabled: isFilterApplied ? false : true,
                iconProps: { iconName: 'ClearFilter' },
                onClick: () => onClickClearFilter()
              },
              ...(savedFiltersMenuItem ? [savedFiltersMenuItem] : [])
            ],
          }
        } 
      );
    }
    return commandBarItems;
  }
  // Mutating styles definition
  const stackStyles: IStackStyles = {
    root: {
      width: `20%`
    },
  };
  return (
    <div>  
      {showToast && (
        <ToastMessage showToast={showToast} type={toastType} message={toastMessage} title={toastTitle}></ToastMessage>
      )}
      <ActiveFilterChips items={getActiveFilterChips()} commandsScrollable={props.keepCommandsVisible} commands={
        <CommandBar
          styles={{ root: { padding: 0, minWidth: props.keepCommandsVisible ? 340 : undefined } }}
          items={createCommandBarItemProps()}
          farItems={[]}
          ariaLabel="Command Bar"
          onReduceData={props.keepCommandsVisible ? () => undefined : undefined}
        />
      } />
      <CoherencePanel
        title="Filter Data By Columns"
        titleText="Filter Data by Columns"
        isOpen={isPanelOpen}
        onDismiss={handleOnPanelClose}
        panelSize={CoherencePanelSize.medium}
        closeButtonAriaLabel="Close"
        onRenderFooter={() => (
          <Stack horizontal >
            <PrimaryButton text="Apply Filter"  onClick={() => onClickApplyFilter(null)}/>
            {savedFiltersEnabled && activePreset && hasAnyFilterValue(getFilterSnapshot()) && (
              <DefaultButton
                text="Update Filter"
                title={`Save changes to "${activePreset.name}"`}
                ariaLabel={`Update saved filter ${activePreset.name}`}
                disabled={isSavingPreset}
                onClick={() => void onClickUpdateActiveFilter()}
              />
            )}
            {savedFiltersEnabled && <DefaultButton text="Save Filter As..." onClick={openSaveFilterDialog} />}
            {!savedFiltersEnabled && !isEmpty(props.defaultFilterSessionKey) && <PrimaryButton text="Save as Default Filter"  onClick={() => onClickSaveUserPref()}/>}
            <DefaultButton text="Clear Filter" onClick={onClickClearFilter} />

          </Stack>
        )} 
      >
        {isPanelOpen ? generateControls() : ""}
      </CoherencePanel>
      {savedFiltersEnabled && (
        <>
          <Dialog
            hidden={!isSaveDialogOpen}
            onDismiss={() => { if (!isSavingPreset) setIsSaveDialogOpen(false); }}
            modalProps={{ isBlocking: true }}
            dialogContentProps={{ type: DialogType.normal, title: 'Save filter as' }}
          >
            <TextField
              label="Name"
              value={saveNameInput}
              maxLength={MAX_SAVED_FILTER_NAME_LENGTH}
              autoFocus
              onChange={(_, value) => { setSaveNameInput(value ?? ''); setSaveNameError(null); }}
              errorMessage={saveNameError ?? undefined}
            />
            <Checkbox
              label="Set as my default"
              checked={saveSetAsDefault}
              onChange={(_, checked) => setSaveSetAsDefault(!!checked)}
              styles={{ root: { marginTop: 12 } }}
            />

            <DialogFooter>
              <PrimaryButton text="Save and Apply" disabled={isSavingPreset} onClick={() => void onClickSaveFilterAs()} />
              <DefaultButton text="Cancel" disabled={isSavingPreset} onClick={() => setIsSaveDialogOpen(false)} />
            </DialogFooter>
          </Dialog>
          <Dialog
            hidden={presetToDelete === null}
            onDismiss={() => { if (!isDeletingPreset) setPresetToDelete(null); }}
            modalProps={{ isBlocking: true }}
            dialogContentProps={{
              type: DialogType.normal,
              title: 'Delete saved filter?',
              subText: `"${lastPresetToDeleteRef.current}" will be deleted. This cannot be undone.`
            }}
          >
            <DialogFooter>
              <PrimaryButton text="Delete" disabled={isDeletingPreset} onClick={() => void onConfirmDeletePreset()} />
              <DefaultButton text="Cancel" disabled={isDeletingPreset} onClick={() => setPresetToDelete(null)} />
            </DialogFooter>
          </Dialog>
        </>
      )}    </div>
  );
};

export default CustomGridControls;
